import {
	clampSurveyPopupHeight,
	SURVEY_POPUP_BACKDROP,
	SURVEY_POPUP_EXPANDED_HEIGHT,
	SURVEY_POPUP_EXPANDED_WIDTH,
	SURVEY_POPUP_RADIUS,
	SURVEY_POPUP_SHADOW,
	SURVEY_POPUP_WIDTH_PX,
} from "@weloop/core/widget/survey-popup-frame";

import type { Locale } from "./i18n";
import type { LauncherRect } from "./launcher-anchor";

import { t } from "./i18n";
import {
	applyAnchor,
	computeLauncherAnchor,
	sideOfLauncher,
} from "./launcher-anchor";

// The one iframe serves two surfaces: the full-height drawer and the small
// survey popup anchored above the FAB. A single mode variable owns which
// styles apply – transitions between surfaces always pass through here.
export type DisplayMode = "drawer" | "hidden" | "popup";

export interface IframeHandle {
	readonly close: () => void;
	readonly dispose: () => void;
	readonly element: HTMLIFrameElement;
	/**
	 * Run the Escape behavior for a key press the host page never saw. A
	 * keydown inside the widget frame stays in that frame, so the widget
	 * reports it with `weloop:escape`.
	 */
	readonly escape: () => void;
	readonly isOpen: () => boolean;
	readonly mode: () => DisplayMode;
	readonly open: () => void;
	readonly resizePopup: (height: number) => void;
	/**
	 * Dock edge for the next open. The draggable bubble can settle on either
	 * side, and the panel must slide out from wherever the bubble sits – not
	 * from the corner the project config named. A surface already on screen
	 * keeps its edge until it closes.
	 */
	readonly setDockSide: (side: "left" | "right") => void;
	/**
	 * Near-full-viewport width while the widget shows a surface that needs
	 * room – the screenshot annotator. Wins over the thread width until the
	 * widget signals the surface closed.
	 */
	readonly setExpanded: (active: boolean) => void;
	/**
	 * Where the launcher rests (see `FabHandle.restingRect`). The survey popup
	 * pins next to it – the corner only fits a bottom-corner bubble with the
	 * default margin; `undefined` (launcher hidden) keeps the corner. Moves a
	 * popup already on screen, never shows or hides one.
	 */
	readonly setLauncher: (launcher: LauncherRect | undefined) => void;
	/**
	 * Grow the survey popup into a centered panel, or return it to the bubble.
	 * A questionnaire with image options has nothing to compare at the
	 * anchored width. Ignored unless a popup is on screen – the drawer owns
	 * its own geometry.
	 */
	readonly setPopupExpanded: (expanded: boolean) => void;
	/**
	 * The reading room: the drawer opened wider on purpose, with a rounded
	 * leading edge and a clear band of dimmed page left beside it. Narrower
	 * than the annotator so it still reads as the panel. Backdrop and Escape
	 * go back to the panel while it is up, not to closed.
	 */
	readonly setReadingRoom: (active: boolean) => void;
	readonly setThreadMode: (active: boolean) => void;
	readonly showPopup: (height: number) => void;
}

export interface IframeOptions {
	// The host page's look, which the widget matches. It also resolves the
	// loading skeleton's `light-dark()` colors, so the placeholder does not
	// flash the OS theme first.
	readonly colorScheme: "dark" | "light";
	// Close requests raised by the chrome itself (backdrop click, Escape).
	// Routed through the caller so the FAB aria state and `close` event
	// subscribers stay in sync – the handle's own `close()` only hides.
	readonly onRequestClose: () => void;
	// Collapse requests raised by the chrome while the popup is expanded.
	// Routed through the caller so the widget – which owns the expansion
	// state – hears about them and keeps its toggle in sync.
	readonly onRequestCollapsePopup: () => void;
	// Back-to-panel requests raised by the chrome while the reading room is
	// open. The widget owns the room, so it hears the request and narrows.
	readonly onRequestCollapseReadingRoom: () => void;
	// Escape over the anchored survey popup. The popup is not the panel, so
	// it never raises `close`; the widget dismisses it like its own cross.
	readonly onRequestDismissPopup: () => void;
	// The panel docks flush to the same edge as the launcher.
	readonly position: "left" | "right";
}

const IFRAME_ID = "weloop-frame";
// Loading placeholder behind the widget document – see where the iframe is
// styled. Shapes only, no text, so it needs no translation. Kept to two
// blocks: every byte here ships on every host page load.
const SKELETON_BLOCK = "linear-gradient(light-dark(#eceef3,#262631) 0 0)";
const SKELETON_BACKGROUND = `${SKELETON_BLOCK} 24px 28px/45% 16px no-repeat,${SKELETON_BLOCK} 24px 76px/calc(100% - 48px) 96px no-repeat,light-dark(#fff,#16161d)`;
const BACKDROP_ID = "weloop-backdrop";

// Mirrors the pilot: the panel is a full-viewport-height drawer docked to
// one edge, resting at 380px and widening to 532px while a conversation
// thread is open.
// The survey popup shares this width, so the two stay one constant.
const RESTING_WIDTH = `${String(SURVEY_POPUP_WIDTH_PX)}px`;
const THREAD_WIDTH = "532px";

// The annotator's width: nearly the whole viewport, capped so the drawer
// still reads as a panel on an ultra-wide monitor.
const EXPANDED_WIDTH = "min(calc(100vw - 24px), 1400px)";

// The reading room: fixed width with at least 240px of page left beside it
// on a desktop, so it reads as the panel opened wider rather than the page.
// `maxWidth` on the iframe keeps the 48px strip on narrow viewports.
const READING_ROOM_WIDTH = "min(1120px, calc(100vw - 240px))";
const READING_ROOM_RADIUS = "20px";
// One ease-out step from the panel width; the widget fades its content in
// once the width has settled.
const READING_ROOM_ANIMATION_MS = 280;

// Survey popup geometry: pinned next to the launcher (see
// `launcher-anchor.ts`), framed with the values in
// `@weloop/core/widget/survey-popup-frame` – the dashboard's survey preview
// draws its stand-in frame from the same ones.
const POPUP_WIDTH = `${String(SURVEY_POPUP_WIDTH_PX)}px`;
const POPUP_RADIUS = SURVEY_POPUP_RADIUS;
const POPUP_SHADOW = SURVEY_POPUP_SHADOW;

// The height a collapse falls back to when the popup was expanded before the
// widget ever reported one. Mirrors the widget's own opening guess.
const FALLBACK_POPUP_HEIGHT = 220;

const POPUP_EXPANDED_WIDTH = SURVEY_POPUP_EXPANDED_WIDTH;
const POPUP_EXPANDED_HEIGHT = SURVEY_POPUP_EXPANDED_HEIGHT;

const OPEN_ANIMATION_MS = 220;

// How long the popup stays transparent waiting for the widget's first real
// measurement before it is revealed at its fallback height anyway.
const MEASURE_GRACE_MS = 120;

// Fast, decelerating slide – starts quick and settles softly.
const OPEN_EASING = "cubic-bezier(0.16, 1, 0.3, 1)";

// Violet-tinted panel shadow from the design; mirrored per docked edge.
const panelShadow = (dockedLeft: boolean): string => {
	const direction = dockedLeft ? "" : "-";
	return (
		`${direction}24px 0 60px hsl(260deg 100% 69% / 16%), ` +
		`${direction}8px 0 24px rgb(0 0 0 / 8%)`
	);
};

// Keep the popup card-sized even when the widget asks for a tall card on a
// short viewport.
const clampPopupHeight = (height: number): number =>
	clampSurveyPopupHeight(height, window.innerHeight);

export const mountIframe = (
	src: string,
	locale: Locale,
	options: IframeOptions,
): IframeHandle => {
	// Theatre mode for the survey popup, and the height it goes back to when
	// it collapses – measurements taken while expanded belong to the wide box.
	// Declared before the chrome below, which reads it on every backdrop
	// click.
	let popupExpanded = false;
	let anchoredPopupHeight = FALLBACK_POPUP_HEIGHT;
	// Set once the visitor moved the launcher; the popup then follows it.
	let launcher: LauncherRect | undefined = undefined;
	// The reading room, declared before the chrome for the same reason.
	let readingRoomActive = false;

	// Dim the host page while the panel is open – lightly and without a blur,
	// so the page stays readable next to the panel. Sits below the FAB
	// (2147483646) so the launcher stays clickable, matching the pilot where
	// launcher and panel share a layer above the scrim.
	const backdrop = document.createElement("div");
	backdrop.id = BACKDROP_ID;
	Object.assign(backdrop.style, {
		background: SURVEY_POPUP_BACKDROP,
		display: "none",
		inset: "0",
		opacity: "0",
		position: "fixed",
		transition: `opacity ${OPEN_ANIMATION_MS}ms ease`,
		zIndex: "2147483645",
	});
	const handleBackdropClick = (): void => {
		// Over an expanded questionnaire the backdrop means "give me the small
		// card back", never "throw my answers away" – dismissing the prompt is
		// recorded as a refusal and the form is not persisted until it is sent.
		if (popupExpanded) {
			options.onRequestCollapsePopup();
			return;
		}
		// Over the reading room the backdrop means "back to the panel".
		if (readingRoomActive) {
			options.onRequestCollapseReadingRoom();
			return;
		}
		options.onRequestClose();
	};
	backdrop.addEventListener("click", handleBackdropClick);
	document.body.append(backdrop);

	// Set while the popup is on screen but still transparent, waiting for the
	// widget's first post-layout height.
	let awaitingPopupMeasure = false;

	// Where the panel docks and slides from. Starts on the configured corner
	// and follows the draggable bubble via `setDockSide`.
	let dockSide: "left" | "right" = options.position;
	const dockedLeft = (): boolean => dockSide === "left";
	const hiddenTransform = (): string =>
		dockedLeft() ? "translateX(-105%)" : "translateX(105%)";
	// The popup fades and slides up in place instead of sliding from the edge.
	const popupHiddenTransform = "translateY(12px)";

	const iframe = document.createElement("iframe");
	iframe.id = IFRAME_ID;
	iframe.src = src;
	iframe.setAttribute("title", t(locale, "title"));
	// `clipboard-write` covers paste-from-clipboard convenience in the
	// composer. `display-capture` is required for `getDisplayMedia()` to
	// resolve from inside the iframe – without it the browser rejects
	// screenshot and screen-recording from the embedded widget. The
	// host page still has to grant the permission prompt; we just have to
	// not block it at the iframe boundary. `fullscreen` lets a video
	// player inside the widget – a recording under review, a clip in the
	// thread – go full screen; without it the browser greys that control
	// out.
	iframe.setAttribute("allow", "clipboard-write; display-capture; fullscreen");
	iframe.allowFullscreen = true;
	Object.assign(iframe.style, {
		// A skeleton painted as the iframe's own background: a title bar and
		// one card, visible until the widget document paints over it, so a
		// slow first load is not an empty panel. `light-dark()` follows the
		// host page's look, like the widget's theme does.
		background: SKELETON_BACKGROUND,
		border: "none",
		bottom: "0",
		boxShadow: panelShadow(dockedLeft()),
		colorScheme: options.colorScheme,
		display: "none",
		height: "100%",
		left: dockedLeft() ? "0" : "auto",
		// Keep a strip of the dimmed host page visible on narrow viewports so
		// the backdrop stays clickable as a close affordance.
		maxWidth: "calc(100vw - 48px)",
		position: "fixed",
		right: dockedLeft() ? "auto" : "0",
		top: "0",
		transform: hiddenTransform(),
		// Under reduced motion the panel only fades: the slide, the width
		// change and the popup's lift are motion, a fade is not. Read once
		// per mount – inline styles cannot carry a media query.
		transition: window.matchMedia?.("(prefers-reduced-motion: reduce)").matches
			? `opacity ${OPEN_ANIMATION_MS}ms ease`
			: `width ${READING_ROOM_ANIMATION_MS}ms ease-out, ` +
				`height ${OPEN_ANIMATION_MS}ms ease, ` +
				`opacity ${OPEN_ANIMATION_MS}ms ease, ` +
				`transform ${OPEN_ANIMATION_MS}ms ${OPEN_EASING}`,
		width: RESTING_WIDTH,
		zIndex: "2147483647",
	});
	document.body.append(iframe);

	let currentMode: DisplayMode = "hidden";
	let threadActive = false;
	let expandedActive = false;
	// Pending close-side cleanup. Cancelled when the panel reopens before the
	// exit slide finishes, so the reopen never fights a stale `display: none`.
	let hideTimer: ReturnType<typeof setTimeout> | undefined;
	// Backdrop fade-out cleanup. Separate from `hideTimer` so collapsing an
	// expanded popup – which drops the backdrop but keeps the card on screen –
	// never cancels a close in flight.
	let backdropTimer: ReturnType<typeof setTimeout> | undefined;

	const drawerWidth = (): string => {
		if (expandedActive) {
			return EXPANDED_WIDTH;
		}
		if (readingRoomActive) {
			return READING_ROOM_WIDTH;
		}
		return threadActive ? THREAD_WIDTH : RESTING_WIDTH;
	};

	// Only the reading room rounds the leading edge; the other drawer widths
	// stay flush with the viewport edge.
	const drawerRadius = (): string => {
		if (!readingRoomActive || expandedActive) {
			return "0";
		}
		return dockedLeft()
			? `0 ${READING_ROOM_RADIUS} ${READING_ROOM_RADIUS} 0`
			: `${READING_ROOM_RADIUS} 0 0 ${READING_ROOM_RADIUS}`;
	};

	// Both sides are always written – the panel may have docked to the other
	// edge on a previous open, and a stale inset would stretch the iframe
	// across the viewport.
	const applyDrawerStyles = (): void => {
		Object.assign(iframe.style, {
			borderRadius: drawerRadius(),
			bottom: "0",
			boxShadow: panelShadow(dockedLeft()),
			height: "100%",
			left: dockedLeft() ? "0" : "auto",
			margin: "0",
			maxWidth: "calc(100vw - 48px)",
			opacity: "1",
			right: dockedLeft() ? "auto" : "0",
			top: "0",
			width: drawerWidth(),
		});
	};

	// Where the popup pins: next to the launcher, on whichever side of it the
	// card fits, or the bubble's corner while the launcher is hidden.
	const applyPopupPosition = (height: number): void => {
		const viewport = { height: window.innerHeight, width: window.innerWidth };
		applyAnchor(
			iframe,
			computeLauncherAnchor(
				launcher === undefined ? dockSide : sideOfLauncher(launcher, viewport),
				launcher,
				viewport,
				clampPopupHeight(height),
			),
			dockSide,
		);
	};

	const applyPopupStyles = (height: number): void => {
		Object.assign(iframe.style, {
			borderRadius: POPUP_RADIUS,
			boxShadow: POPUP_SHADOW,
			height: `${String(clampPopupHeight(height))}px`,
			margin: "0",
			maxWidth: "calc(100vw - 32px)",
			width: POPUP_WIDTH,
		});
		applyPopupPosition(height);
	};

	// Centered on the viewport: `inset: 0` with auto margins centers a fixed
	// element at its own size, leaving `transform` to the open and close
	// animations that already own it.
	const applyExpandedPopupStyles = (): void => {
		Object.assign(iframe.style, {
			borderRadius: POPUP_RADIUS,
			bottom: "0",
			boxShadow: POPUP_SHADOW,
			height: POPUP_EXPANDED_HEIGHT,
			left: "0",
			margin: "auto",
			maxWidth: "calc(100vw - 32px)",
			right: "0",
			top: "0",
			width: POPUP_EXPANDED_WIDTH,
		});
	};

	const cancelHide = (): void => {
		if (hideTimer !== undefined) {
			clearTimeout(hideTimer);
			hideTimer = undefined;
		}
	};

	const cancelBackdropHide = (): void => {
		if (backdropTimer !== undefined) {
			clearTimeout(backdropTimer);
			backdropTimer = undefined;
		}
	};

	const fadeOutBackdrop = (): void => {
		cancelBackdropHide();
		backdrop.style.opacity = "0";
		backdropTimer = setTimeout(() => {
			backdropTimer = undefined;
			backdrop.style.display = "none";
		}, OPEN_ANIMATION_MS);
	};

	const openFn = (): void => {
		if (currentMode === "drawer") {
			return;
		}
		const fromPopup = currentMode === "popup";
		currentMode = "drawer";
		popupExpanded = false;
		cancelHide();
		cancelBackdropHide();
		applyDrawerStyles();
		backdrop.style.display = "block";
		iframe.style.display = "block";
		if (!fromPopup) {
			// Start off-screen so the display change and the slide don't
			// coalesce into one skipped transition.
			iframe.style.transform = hiddenTransform();
		}
		// Synchronous reflow so the `display`/start-state changes commit
		// before the animated properties move. A rAF would also work but
		// never fires in hidden tabs, which would leave the panel at
		// opacity 0 until the tab regains focus.
		void iframe.offsetWidth;
		backdrop.style.opacity = "1";
		iframe.style.transform = "translateX(0) translateY(0)";
		iframe.focus();
	};

	const close = (): void => {
		if (currentMode === "hidden") {
			return;
		}
		const wasPopup = currentMode === "popup";
		currentMode = "hidden";
		popupExpanded = false;
		cancelBackdropHide();
		// Reverse of the open: slide the surface out (drawer to its edge,
		// popup down while fading), then drop it from the layout once the
		// transition has played out. The timer (not `transitionend`) so a
		// hidden tab still ends hidden.
		backdrop.style.opacity = "0";
		if (wasPopup) {
			iframe.style.opacity = "0";
			iframe.style.transform = popupHiddenTransform;
		} else {
			iframe.style.transform = hiddenTransform();
		}
		hideTimer = setTimeout(() => {
			hideTimer = undefined;
			backdrop.style.display = "none";
			iframe.style.display = "none";
			iframe.style.opacity = "1";
			iframe.style.transform = hiddenTransform();
		}, OPEN_ANIMATION_MS);
	};

	const revealPopup = (): void => {
		awaitingPopupMeasure = false;
		iframe.style.opacity = "1";
		iframe.style.transform = "translateY(0)";
	};

	const showPopup = (height: number): void => {
		// The drawer always wins – a survey never interrupts an open panel.
		if (currentMode !== "hidden") {
			return;
		}
		currentMode = "popup";
		popupExpanded = false;
		anchoredPopupHeight = height;
		cancelHide();
		applyPopupStyles(height);
		iframe.style.opacity = "0";
		iframe.style.transform = popupHiddenTransform;
		iframe.style.display = "block";
		void iframe.offsetWidth;
		// The card has no layout while the iframe is `display: none`, so the
		// height above is the widget's fallback guess. Stay transparent until
		// it re-measures itself with real layout and sends the true height –
		// otherwise the prompt visibly jumps to size after it appears. The
		// timeout reveals it anyway if that message never lands (a background
		// tab suspends the widget's ResizeObserver).
		awaitingPopupMeasure = true;
		setTimeout(() => {
			if (awaitingPopupMeasure && currentMode === "popup") {
				revealPopup();
			}
		}, MEASURE_GRACE_MS);
		// No backdrop and no focus steal – the prompt must not interrupt
		// whatever the user is doing on the host page.
	};

	const setPopupExpanded = (expanded: boolean): void => {
		if (currentMode !== "popup" || popupExpanded === expanded) {
			return;
		}
		popupExpanded = expanded;
		if (expanded) {
			applyExpandedPopupStyles();
			// A centered panel over the page is a dialog: dim what is behind it.
			cancelBackdropHide();
			backdrop.style.display = "block";
			void iframe.offsetWidth;
			backdrop.style.opacity = "1";
			return;
		}
		applyPopupStyles(anchoredPopupHeight);
		fadeOutBackdrop();
	};

	const escape = (): void => {
		if (currentMode === "hidden") {
			return;
		}
		// Escape backs out one step at a time: an expanded questionnaire
		// collapses, and only a second press dismisses the prompt.
		if (popupExpanded) {
			options.onRequestCollapsePopup();
			return;
		}
		if (currentMode === "popup") {
			options.onRequestDismissPopup();
			return;
		}
		if (readingRoomActive) {
			options.onRequestCollapseReadingRoom();
			return;
		}
		options.onRequestClose();
	};

	// The anchored popup does not take focus, so an Escape on the host page
	// is meant for the page – a menu or a dialog of its own – unless focus is
	// in the prompt. The expanded popup dims the page behind it like the
	// panel, so Escape there backs out of it. A key the page already handled
	// is never taken over.
	// oxlint-disable-next-line typescript-eslint/prefer-readonly-parameter-types -- KeyboardEvent is an external mutable DOM interface
	const handleKey = (event: KeyboardEvent): void => {
		if (event.key !== "Escape" || event.defaultPrevented) {
			return;
		}
		if (
			currentMode === "popup" &&
			!popupExpanded &&
			document.activeElement !== iframe
		) {
			return;
		}
		escape();
	};

	document.addEventListener("keydown", handleKey);

	return {
		close,
		dispose: () => {
			cancelHide();
			cancelBackdropHide();
			document.removeEventListener("keydown", handleKey);
			backdrop.removeEventListener("click", handleBackdropClick);
			backdrop.remove();
			iframe.remove();
		},
		element: iframe,
		escape,
		isOpen: () => currentMode === "drawer",
		mode: () => currentMode,
		open: openFn,
		resizePopup: (height) => {
			if (currentMode !== "popup") {
				return;
			}
			// A card measured inside the expanded panel says nothing about the
			// narrow box it will collapse back into, so the measurement is
			// dropped rather than stored.
			if (popupExpanded) {
				return;
			}
			anchoredPopupHeight = height;
			iframe.style.height = `${String(clampPopupHeight(height))}px`;
			// A taller card may no longer fit on the side it was pinned to.
			applyPopupPosition(height);
			if (awaitingPopupMeasure) {
				revealPopup();
			}
		},
		setDockSide: (side) => {
			// A visible surface keeps its edge – switching insets mid-open would
			// teleport the panel across the viewport. The caller sets the side
			// again on the next open.
			if (currentMode !== "hidden") {
				return;
			}
			dockSide = side;
		},
		setExpanded: (active) => {
			expandedActive = active;
			// In popup mode the width belongs to the popup; the drawer width is
			// re-applied on the next open.
			if (currentMode !== "popup") {
				iframe.style.width = drawerWidth();
			}
		},
		setLauncher: (next) => {
			launcher = next;
			if (currentMode === "popup" && !popupExpanded) {
				applyPopupPosition(anchoredPopupHeight);
			}
		},
		setPopupExpanded,
		setReadingRoom: (active) => {
			readingRoomActive = active;
			if (currentMode !== "popup") {
				iframe.style.width = drawerWidth();
				iframe.style.borderRadius = drawerRadius();
			}
		},
		setThreadMode: (active) => {
			threadActive = active;
			if (currentMode !== "popup") {
				iframe.style.width = drawerWidth();
			}
		},
		showPopup,
	};
};
